import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMailboxSyncStatusQuery } from "../api/queries/userQueries";
import { toast } from "sonner";
import { useUserInfoQuery } from "../api/queries/userQueries";

/**
 * Hook to monitor mailbox sync status and show error toasts when sync fails.
 * Should be used in a top-level component like AppSkeleton.
 */
export function useMailboxSyncStatusMonitor() {
  const navigate = useNavigate();
  const { data: syncStatus } = useMailboxSyncStatusQuery();
  const { data: userInfo } = useUserInfoQuery();
  const hasShownErrorToastRef = useRef<string | null>(null);

  useEffect(() => {
    if (!syncStatus || !userInfo) return;

    const errorKey = `${syncStatus.integration_status}-${syncStatus.last_error || ""}`;

    // Show error toast if there's a sync error and we haven't shown this specific error yet
    if (syncStatus.has_error && hasShownErrorToastRef.current !== errorKey) {
      const provider =
        userInfo.oauth_provider === "google" ? "Gmail" : "Outlook";

      toast.error("Email Sync Error", {
        description:
          syncStatus.last_error ||
          `Your ${provider} mailbox is not syncing. Please reconnect your email account in settings.`,
        duration: 10000, // Show for 10 seconds
        action: {
          label: "Reconnect",
          onClick: () => {
            navigate("/settings?tab=email-sync");
          },
        },
      });

      hasShownErrorToastRef.current = errorKey;
    }

    // Reset toast ref if sync becomes healthy again
    if (!syncStatus.has_error && hasShownErrorToastRef.current) {
      hasShownErrorToastRef.current = null;
    }
  }, [syncStatus, userInfo]);
}
